import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  EventMatchJobStatus,
  EventMatchJobStatusLabel,
  EventUserEntity,
  PartnerMatchResult,
  UserFacingTopic,
  eventApi,
  useFindEventMatchResultsByUserQuery,
  useFindEventUsersQuery,
  useFindOneEventQuery,
  useRedoEventPairMatchMutation,
  useRunEventMatchMutation,
  useMatchEventUserMutation,
  useNotifyEventFeedbackMutation,
} from 'src/store/eventApi';
import { Button } from 'src/ui/Button';
import { Badge } from 'src/ui/Badge';
import { Spinner } from 'src/ui/Spinner';
import { Modal } from 'src/ui/Modal';
import { Select } from 'src/ui/Select';
import { Textarea } from 'src/ui/Textarea';
import { Field } from 'src/ui/Field';
import { Icon } from 'src/ui/Icon';
import { EmptyState } from 'src/ui/EmptyState';
import { useToast } from 'src/ui/Toast';
import { NEW_EVENT_ID } from './index';
import AttendeeDetailModal from './attendeeDetailModal';
import FeedbackMessageModal from './feedbackMessageModal';
import { GENDER_STYLE, GenderTag } from './genderStyle';
import { assignSeats, SeatAssignment } from './seatNumbers';
import { getAttendeeDisplayName } from 'src/util';

interface MatchResultsProps {
  eventId: string | null;
}

type PairJobStatus = 'pending' | 'done' | 'failed';

interface PairJob {
  id: number;
  userId1: string;
  userId2: string;
  name1: string;
  name2: string;
  topicFineTune?: string;
  status: PairJobStatus;
  startedAt: number;
  finishedAt?: number;
  error?: string;
}

const POLL_INTERVAL_MS = 5000;
const MAX_PAIR_LOG = 10;

const attendeeLabel = (eu?: EventUserEntity) => getAttendeeDisplayName(eu);

const formatTime = (ms: number) => new Date(ms).toLocaleTimeString();

/** What the server means when an incremental run does nothing. */
const MATCH_ONE_REFUSAL: Record<string, string> = {
  not_participant: 'Not a confirmed participant of this event',
  not_eligible: 'Profile is not complete enough to match yet',
  unpairable: 'No profile gender to pair on',
  busy: 'Another match run is working on this event — try again shortly',
};

const MATCH_JOB_BADGE_COLOR: Record<EventMatchJobStatus, string> = {
  [EventMatchJobStatus.NOT_STARTED]: 'gray',
  [EventMatchJobStatus.IN_PROGRESS]: 'amber',
  [EventMatchJobStatus.COMPLETED]: 'green',
  [EventMatchJobStatus.FAILED]: 'red',
};

const MatchResults = ({ eventId }: MatchResultsProps) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const isEmpty = !eventId;
  const isNew = eventId === NEW_EVENT_ID;
  const skip = isEmpty || isNew;
  const safeEventId = eventId ?? '';

  const { data: event } = useFindOneEventQuery(safeEventId, { skip });
  const { data: attendees } = useFindEventUsersQuery(safeEventId, { skip });
  const [runEventMatch, runStatus] = useRunEventMatchMutation();
  const [notifyFeedback, notifyStatus] = useNotifyEventFeedbackMutation();
  const [redoEventPairMatch] = useRedoEventPairMatchMutation();
  const [pairModalOpen, setPairModalOpen] = useState(false);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [viewEventUserId, setViewEventUserId] = useState<number | null>(null);
  const [pairJobs, setPairJobs] = useState<PairJob[]>([]);

  const onViewUser = (userId: string) => {
    const eu = attendees?.find((a) => a.userId === userId);
    if (!eu) return;
    setViewEventUserId(eu.id);
    setDetailOpen(true);
  };

  const matchJobStatus = event?.matchJobStatus;

  // Poll the event while a match job is in progress so the badge updates live
  useEffect(() => {
    if (skip) return;
    if (matchJobStatus !== EventMatchJobStatus.IN_PROGRESS) return;
    const t = setInterval(() => {
      dispatch(
        eventApi.util.invalidateTags([{ type: 'Events', id: safeEventId }])
      );
    }, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [skip, matchJobStatus, safeEventId, dispatch]);

  const attendeeMap = useMemo(() => {
    const m = new Map<string, EventUserEntity>();
    (attendees ?? []).forEach((eu) => m.set(eu.userId, eu));
    return m;
  }, [attendees]);

  const seatMap = useMemo(() => assignSeats(attendees ?? []), [attendees]);

  const sortedAttendees = useMemo(() => {
    const arr = [...(attendees ?? [])];
    arr.sort((a, b) => {
      const sa = seatMap.get(a.userId)?.seat;
      const sb = seatMap.get(b.userId)?.seat;
      if (!sa && !sb) return 0;
      if (!sa) return 1;
      if (!sb) return -1;
      return sa.localeCompare(sb);
    });
    return arr;
  }, [attendees, seatMap]);

  if (isEmpty) {
    return (
      <div
        style={{
          padding: 16,
          color: 'var(--text-muted)',
          fontSize: 'var(--fs-sm)',
        }}
      >
        Select an event to view match results.
      </div>
    );
  }
  if (isNew) {
    return (
      <div
        style={{
          padding: 16,
          color: 'var(--text-muted)',
          fontSize: 'var(--fs-sm)',
        }}
      >
        Save the event first.
      </div>
    );
  }

  const onRunFullMatch = async () => {
    try {
      await runEventMatch(safeEventId).unwrap();
      toast({ type: 'info', title: 'Match job started' });
    } catch (e: any) {
      toast({
        type: 'error',
        title: 'Failed to start match',
        message: e?.data?.message || e?.message,
      });
    }
  };

  // Post-event nudge. The server refuses outside the like/pass window, so the
  // button leans on that rather than second-guessing the dates here.
  const onNotifyFeedback = async () => {
    try {
      const { notified } = await notifyFeedback(safeEventId).unwrap();
      toast({
        type: 'success',
        title: notified
          ? `Reminder sent to ${notified} guest${notified === 1 ? '' : 's'}`
          : 'No guests to remind',
      });
    } catch (e: any) {
      toast({
        type: 'error',
        title: 'Failed to send reminder',
        message: e?.data?.message || e?.message,
      });
    }
  };

  const onRunPair = async (
    userId1: string,
    userId2: string,
    topicFineTune?: string
  ): Promise<boolean> => {
    const jobId = Date.now();
    const job: PairJob = {
      id: jobId,
      userId1,
      userId2,
      name1: attendeeLabel(attendeeMap.get(userId1)),
      name2: attendeeLabel(attendeeMap.get(userId2)),
      topicFineTune,
      status: 'pending',
      startedAt: jobId,
    };
    setPairJobs((prev) => [job, ...prev].slice(0, MAX_PAIR_LOG));
    try {
      await redoEventPairMatch({
        eventId: safeEventId,
        userId1,
        userId2,
        topicFineTune,
      }).unwrap();
      setPairJobs((prev) =>
        prev.map((p) =>
          p.id === jobId ? { ...p, status: 'done', finishedAt: Date.now() } : p
        )
      );
      toast({ type: 'success', title: 'Pair match updated' });
      return true;
    } catch (e: any) {
      const message = e?.data?.message || e?.message || 'Unknown error';
      setPairJobs((prev) =>
        prev.map((p) =>
          p.id === jobId
            ? { ...p, status: 'failed', finishedAt: Date.now(), error: message }
            : p
        )
      );
      toast({ type: 'error', title: 'Pair match failed', message });
      return false;
    }
  };

  const fullStatus = matchJobStatus ?? EventMatchJobStatus.NOT_STARTED;
  const runDisabled =
    runStatus.isLoading ||
    fullStatus === EventMatchJobStatus.IN_PROGRESS ||
    fullStatus === EventMatchJobStatus.COMPLETED;

  return (
    <div className="col" style={{ height: '100%', overflow: 'hidden' }}>
      {/* Match jobs header */}
      <div
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg-muted)',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            fontWeight: 600,
            fontSize: 'var(--fs-sm)',
            marginBottom: 8,
          }}
        >
          Match Jobs
        </div>
        <div className="row gap-2" style={{ flexWrap: 'wrap' }}>
          <span className="text-secondary" style={{ fontSize: 'var(--fs-sm)' }}>
            Full event match:
          </span>
          <Badge color={MATCH_JOB_BADGE_COLOR[fullStatus]} size="sm">
            {EventMatchJobStatusLabel[fullStatus]}
          </Badge>
          <Button
            variant="primary"
            size="sm"
            leftIcon="rotate-cw"
            onClick={onRunFullMatch}
            loading={runStatus.isLoading}
            disabled={runDisabled}
          >
            Run match
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setPairModalOpen(true)}
          >
            Run pair match
          </Button>
          <Button
            variant="secondary"
            size="sm"
            leftIcon="send"
            onClick={onNotifyFeedback}
            loading={notifyStatus.isLoading}
            disabled={notifyStatus.isLoading}
            title="Push every seated guest a reminder that they can now like or pass their matches. Available for 7 days after the event ends."
          >
            Send feedback reminder
          </Button>
          <Button
            variant="ghost"
            size="sm"
            leftIcon="pencil"
            onClick={() => setMessageModalOpen(true)}
          >
            Edit message
          </Button>
        </div>

        {pairJobs.length > 0 && (
          <div
            style={{
              marginTop: 10,
              borderTop: '1px solid var(--border)',
              paddingTop: 8,
              maxHeight: 140,
              overflowY: 'auto',
            }}
          >
            <div
              className="text-muted"
              style={{
                fontSize: 'var(--fs-xs)',
                fontWeight: 600,
                marginBottom: 4,
              }}
            >
              Recent pair runs (this session)
            </div>
            <div className="col gap-1">
              {pairJobs.map((j) => (
                <PairJobRow key={j.id} job={j} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Attendee match list */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {sortedAttendees.length === 0 ? (
          <EmptyState icon="users" title="No attendees yet" />
        ) : (
          sortedAttendees.map((eu) => (
            <AttendeeMatchCard
              key={eu.id}
              eventId={safeEventId}
              attendee={eu}
              attendeeMap={attendeeMap}
              seatMap={seatMap}
              onViewUser={onViewUser}
            />
          ))
        )}
      </div>

      <RunPairModal
        isOpen={pairModalOpen}
        onClose={() => setPairModalOpen(false)}
        attendees={sortedAttendees}
        seatMap={seatMap}
        onSubmit={onRunPair}
      />

      <AttendeeDetailModal
        eventUser={attendees?.find((a) => a.id === viewEventUserId) ?? null}
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
      />

      <FeedbackMessageModal
        isOpen={messageModalOpen}
        onClose={() => setMessageModalOpen(false)}
      />
    </div>
  );
};

const PairJobRow = ({ job }: { job: PairJob }) => {
  const icon =
    job.status === 'pending' ? (
      <Spinner size="sm" />
    ) : job.status === 'done' ? (
      <Icon name="check-circle" size={12} />
    ) : (
      <Icon name="alert-triangle" size={12} />
    );
  return (
    <div className="row gap-2" style={{ fontSize: 'var(--fs-xs)' }}>
      <span
        style={{
          width: 14,
          textAlign: 'center',
          color:
            job.status === 'done'
              ? 'var(--green-text)'
              : job.status === 'failed'
              ? 'var(--red-text)'
              : undefined,
        }}
      >
        {icon}
      </span>
      <span
        style={{
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {job.name1} ↔ {job.name2}
      </span>
      {job.topicFineTune && (
        <span
          className="text-muted truncate"
          style={{ maxWidth: 120 }}
          title={job.topicFineTune}
        >
          "{job.topicFineTune}"
        </span>
      )}
      <span className="text-muted">{formatTime(job.startedAt)}</span>
    </div>
  );
};

interface RunPairModalProps {
  isOpen: boolean;
  onClose: () => void;
  attendees: EventUserEntity[];
  seatMap: Map<string, SeatAssignment>;
  onSubmit: (
    userId1: string,
    userId2: string,
    topicFineTune?: string
  ) => Promise<boolean>;
}

const RunPairModal = ({
  isOpen,
  onClose,
  attendees,
  seatMap,
  onSubmit,
}: RunPairModalProps) => {
  const [u1, setU1] = useState('');
  const [u2, setU2] = useState('');
  const [hint, setHint] = useState('');
  const opened = useRef(false);

  useEffect(() => {
    if (isOpen && !opened.current) {
      setU1('');
      setU2('');
      setHint('');
      opened.current = true;
    } else if (!isOpen) {
      opened.current = false;
    }
  }, [isOpen]);

  const optionLabel = (eu: EventUserEntity) => {
    const gender = eu.user?.profile?.gender;
    const base = attendeeLabel(eu);
    const symbol =
      typeof gender === 'number' ? GENDER_STYLE[gender]?.symbol : undefined;
    const seat = seatMap.get(eu.userId)?.seat;
    const named = seat ? `${seat} · ${base}` : base;
    return symbol ? `${named} · ${symbol}` : named;
  };

  const handleSubmit = () => {
    if (!u1 || !u2 || u1 === u2) return;
    void onSubmit(u1, u2, hint.trim() || undefined);
    onClose();
  };

  const sameUser = !!u1 && !!u2 && u1 === u2;

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="Run pair match"
      footer={
        <div className="row gap-2" style={{ justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!u1 || !u2 || sameUser}
            onClick={handleSubmit}
          >
            Run pair match
          </Button>
        </div>
      }
    >
      <div className="col gap-3">
        <Field label="Attendee A">
          <Select value={u1} onChange={(e) => setU1(e.target.value)}>
            <option value="">Select attendee A</option>
            {attendees.map((eu) => (
              <option key={eu.userId} value={eu.userId}>
                {optionLabel(eu)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Attendee B">
          <Select value={u2} onChange={(e) => setU2(e.target.value)}>
            <option value="">Select attendee B</option>
            {attendees.map((eu) => (
              <option key={eu.userId} value={eu.userId}>
                {optionLabel(eu)}
              </option>
            ))}
          </Select>
          {sameUser && (
            <div
              style={{
                fontSize: 'var(--fs-xs)',
                color: 'var(--red-text)',
                marginTop: 4,
              }}
            >
              Pick a different second attendee.
            </div>
          )}
        </Field>
        <Field label="Topic fine-tune" hint="Optional — e.g. focus on travel">
          <Textarea
            rows={3}
            placeholder="e.g. focus on travel"
            value={hint}
            onChange={(e) => setHint(e.target.value)}
          />
        </Field>
      </div>
    </Modal>
  );
};

interface CardProps {
  eventId: string;
  attendee: EventUserEntity;
  attendeeMap: Map<string, EventUserEntity>;
  seatMap: Map<string, SeatAssignment>;
  onViewUser: (userId: string) => void;
}

const formatScore = (s: number | undefined | null) =>
  s == null ? '—' : `${Math.round(s)}`;

const TOPIC_BADGE: Record<
  UserFacingTopic['type'],
  { label: string; color: string }
> = {
  推荐理由: { label: 'Recommendation', color: 'purple' },
  话题: { label: 'Topic', color: 'blue' },
  硬伤提示: { label: 'Dealbreaker', color: 'red' },
};

interface MatchBoxProps {
  match: PartnerMatchResult;
  rank: number;
  partner: EventUserEntity | undefined;
  partnerSeat?: string;
  attendeeName: string;
  onViewUser: (userId: string) => void;
}

const MatchBox = ({
  match: m,
  rank,
  partner,
  partnerSeat,
  attendeeName,
  onViewUser,
}: MatchBoxProps) => {
  const [open, setOpen] = useState(false);
  const [reasonOpen, setReasonOpen] = useState(false);
  const partnerName = getAttendeeDisplayName(partner) || m.partnerId;
  const partnerGender = partner?.user?.profile?.gender;

  return (
    <div
      style={{
        padding: 8,
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--bg-muted)',
      }}
    >
      <div
        className="row gap-2"
        style={{
          alignItems: 'center',
          cursor: 'pointer',
          marginBottom: open ? 4 : 0,
        }}
        onClick={() => setOpen((v) => !v)}
      >
        <span
          className="text-muted"
          style={{ fontSize: 'var(--fs-sm)', minWidth: 24 }}
        >
          #{rank}
        </span>
        {partnerSeat && (
          <span
            className="mono badge badge-purple badge-sm"
            style={{ fontWeight: 700 }}
          >
            {partnerSeat}
          </span>
        )}
        <GenderTag gender={partnerGender} />
        <div className="grow" style={{ minWidth: 0, overflow: 'hidden' }}>
          <span
            style={{
              fontWeight: 500,
              fontSize: 'var(--fs-md)',
              cursor: 'pointer',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            }}
            onClick={(e) => {
              e.stopPropagation();
              onViewUser(m.partnerId);
            }}
          >
            {partnerName}
          </span>
        </div>
        {m.hasDealbreaker && (
          <div
            className="row gap-1"
            style={{ color: 'var(--red-text)', fontSize: 'var(--fs-sm)' }}
          >
            <Icon name="alert-triangle" size={13} />
            <span style={{ fontWeight: 500 }}>Dealbreaker</span>
          </div>
        )}
        <span
          className="badge badge-blue"
          style={{ fontSize: 'var(--fs-xs)' }}
          title={`${attendeeName} → ${partnerName}: ${formatScore(
            m.myScore
          )}\n${partnerName} → ${attendeeName}: ${formatScore(m.partnerScore)}`}
        >
          {formatScore(m.myScore)} / {formatScore(m.partnerScore)}
        </span>
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} />
      </div>

      {open && (
        <div>
          {m.myReason && (
            <div style={{ marginTop: 4 }}>
              <div
                className="row gap-1"
                style={{
                  cursor: 'pointer',
                  fontSize: 'var(--fs-sm)',
                  fontWeight: 500,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setReasonOpen((v) => !v);
                }}
              >
                <span className="text-secondary">Match Analysis</span>
                <Icon
                  name={reasonOpen ? 'chevron-down' : 'chevron-right'}
                  size={12}
                />
              </div>
              {reasonOpen && (
                <div
                  className="text-secondary"
                  style={{
                    fontSize: 'var(--fs-sm)',
                    marginTop: 4,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {m.myReason}
                </div>
              )}
            </div>
          )}
          {m.topics.length > 0 && (
            <div className="col gap-2" style={{ marginTop: 8 }}>
              {m.topics.map((topic, topicIdx) => (
                <div key={`${m.resultId}-${topicIdx}`}>
                  <div className="row gap-2" style={{ alignItems: 'center' }}>
                    <Badge
                      color={TOPIC_BADGE[topic.type]?.color ?? 'gray'}
                      size="sm"
                    >
                      {TOPIC_BADGE[topic.type]?.label ?? topic.type}
                    </Badge>
                    <span
                      className="text-secondary"
                      style={{ fontSize: 'var(--fs-sm)', fontWeight: 500 }}
                    >
                      {topic.title}
                    </span>
                  </div>
                  {topic.question && (
                    <div
                      className="text-secondary"
                      style={{
                        fontSize: 'var(--fs-sm)',
                        marginTop: 2,
                        marginLeft: 4,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {topic.question}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Whether this guest's profile is complete enough to be matched. Red is not a
 * warning about them — it is why they have no matches, which an admin
 * otherwise has to guess at from an empty card.
 */
const CompletenessChip = ({ attendee }: { attendee: EventUserEntity }) => {
  const percentage = attendee.profileCompleteness;
  if (percentage == null) return null;
  const eligible = attendee.matchEligible !== false;
  return (
    <Badge color={eligible ? 'green' : 'red'} size="sm">
      {`${percentage}%`}
      {eligible ? '' : ' · too sparse to match'}
    </Badge>
  );
};

const AttendeeMatchCard = ({
  eventId,
  attendee,
  attendeeMap,
  seatMap,
  onViewUser,
}: CardProps) => {
  const [open, setOpen] = useState(true);
  const toast = useToast();
  const { data, isLoading, isFetching } = useFindEventMatchResultsByUserQuery({
    eventId,
    userId: attendee.userId,
  });
  const [matchEventUser, matchStatus] = useMatchEventUserMutation();

  // The server decides who is matchable and says why it did nothing, so the
  // outcome is reported rather than second-guessed here.
  const onMatchOne = async () => {
    try {
      const out = await matchEventUser({
        eventId,
        userId: attendee.userId,
      }).unwrap();
      const message =
        out.status !== 'ok'
          ? MATCH_ONE_REFUSAL[out.status]
          : out.paired === 0
          ? 'Already matched with everyone eligible'
          : `${out.paired} new pair${out.paired === 1 ? '' : 's'}` +
            (out.notified ? `, ${out.notified} notified` : '');
      toast({
        type: out.status === 'ok' ? 'success' : 'info',
        title: getAttendeeDisplayName(attendee),
        message,
      });
    } catch (e: any) {
      toast({
        type: 'error',
        title: 'Match failed',
        message: e?.data?.message || e?.message,
      });
    }
  };

  const matches = data?.matches ?? [];
  const displayName = getAttendeeDisplayName(attendee);
  const profileName = attendee.user?.profile?.name;
  const email = attendee.user?.email;
  const gender = attendee.user?.profile?.gender;
  const seat = seatMap.get(attendee.userId)?.seat;

  return (
    <div
      style={{
        padding: 12,
        borderBottom: '1px solid var(--border)',
      }}
    >
      <div
        className="row gap-2"
        style={{
          alignItems: 'center',
          cursor: 'pointer',
          marginBottom: open ? 8 : 0,
        }}
        onClick={() => setOpen((v) => !v)}
      >
        {seat && (
          <span className="mono badge badge-purple" style={{ fontWeight: 700 }}>
            {seat}
          </span>
        )}
        <GenderTag gender={gender} />
        <div className="grow" style={{ minWidth: 0 }}>
          <span
            style={{
              fontWeight: 500,
              fontSize: 'var(--fs-sm)',
              cursor: 'pointer',
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            onClick={(e) => {
              e.stopPropagation();
              onViewUser(attendee.userId);
            }}
          >
            {displayName}
          </span>
          {profileName && profileName !== displayName && (
            <span
              className="text-muted truncate"
              style={{ fontSize: 'var(--fs-xs)', display: 'block' }}
            >
              {profileName}
              {email ? ` · ${email}` : ''}
            </span>
          )}
          {(!profileName || profileName === displayName) && email && (
            <span
              className="text-muted truncate"
              style={{ fontSize: 'var(--fs-xs)', display: 'block' }}
            >
              {email}
            </span>
          )}
        </div>
        <CompletenessChip attendee={attendee} />
        {(isLoading || isFetching) && <Spinner size="sm" />}
        <Button
          variant="secondary"
          size="sm"
          leftIcon="rotate-cw"
          onClick={(e) => {
            e.stopPropagation();
            void onMatchOne();
          }}
          loading={matchStatus.isLoading}
          disabled={matchStatus.isLoading || attendee.matchEligible === false}
          title={
            attendee.matchEligible === false
              ? 'Profile is not complete enough to match'
              : 'Match this guest with everyone else on the roster'
          }
        >
          Match
        </Button>
        <Button
          variant="secondary"
          size="sm"
          leftIcon="external-link"
          onClick={(e) => {
            e.stopPropagation();
            window.open(
              `/events/${eventId}/print-card/${attendee.userId}`,
              '_blank',
              'noopener,noreferrer'
            );
          }}
          disabled={matches.length === 0}
        >
          Print card
        </Button>
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} />
      </div>

      {open &&
        (matches.length === 0 ? (
          <div className="text-muted" style={{ fontSize: 'var(--fs-xs)' }}>
            No match results yet.
          </div>
        ) : (
          <div className="col gap-2">
            {matches.map((m, idx) => (
              <MatchBox
                key={m.resultId}
                match={m}
                rank={idx + 1}
                partner={attendeeMap.get(m.partnerId)}
                partnerSeat={seatMap.get(m.partnerId)?.seat}
                attendeeName={displayName}
                onViewUser={onViewUser}
              />
            ))}
          </div>
        ))}
    </div>
  );
};

export default MatchResults;
